import React, { useEffect, useRef, useState } from 'react'
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Send, Loader2, Upload, X, FileText, Image } from 'lucide-react'
import { getApiUrl } from '@/config/mercadopago'
import {
  CAMPOS_BOOLEANOS_OBRIGATORIOS,
  ROTULOS_CAMPO_BOOLEANO,
  CAMPO_BOOLEANO_PARA_ID_SCROLL,
  obterErroValidacaoFormulario,
} from './formularioMedicoValidation'
const FormularioMedico = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [exames, setExames] = useState([])
  const [totalSize, setTotalSize] = useState(0)
  const [dragActive, setDragActive] = useState(false)
  const [campoBooleanoPendente, setCampoBooleanoPendente] = useState(null)
  const ultimoGrupoPendenteElRef = useRef(null)

  const marcarPrimeiroRadioPendente = (radioId) => {
    const prevEl = ultimoGrupoPendenteElRef.current
    if (prevEl) {
      prevEl.style.outline = ''
      prevEl.style.outlineOffset = ''
      prevEl.style.borderRadius = ''
      prevEl.style.padding = ''
      prevEl.style.width = ''
      prevEl.style.boxSizing = ''
    }

    if (!radioId) {
      ultimoGrupoPendenteElRef.current = null
      return
    }

    const radioEl = document.getElementById(radioId)
    if (!radioEl) return

    const groupEl = radioEl.closest('[role="radiogroup"]') || radioEl.parentElement
    if (!groupEl) return

    groupEl.style.outline = '3px solid #ef4444'
    groupEl.style.outlineOffset = '4px'
    groupEl.style.borderRadius = '10px'
    groupEl.style.padding = '8px'
    groupEl.style.width = 'calc(100% - 670px)'
    groupEl.style.boxSizing = 'border-box'
    ultimoGrupoPendenteElRef.current = groupEl
  }

  useEffect(() => {
    const el = document?.documentElement
    if (!el) return

    const prev = el.style.scrollPaddingTop
    el.style.scrollPaddingTop = '200px'

    return () => {
      el.style.scrollPaddingTop = prev
    }
  }, [])
  const [formData, setFormData] = useState({
    nomeCompleto: '',
    dataNascimento: '',
    sexo: '',
    idade: '',
    peso: '',
    altura: '',
    alergicoMedicamento: '',
    comentario: '',
    usoMedicamentoDiario: '',
    medicamentosEmUso: '',
    usoCalmantes: '',
    observacoes: '',
    tosseCatarro: '',
    doencaCardiaca: '',
    marcapasso: '',
    problemaPressao: '',
    problemaPulmao: '',
    problemaSangramento: '',
    diabetes: '',
    doencaInfectoContagiosa: '',
    qualDoencaInfecto: '',
    dorColuna: '',
    problemaFigado: '',
    problemaRim: '',
    problemaSangue: '',
    problemaTireoide: '',
    problemaEstomago: '',
    dificuldadeAbrirBoca: '',
    problemaDentario: '',
    usoDrogas: '',
    qualDroga: '',
    problemaNeurologico: '',
    cirurgiasAnteriores: '',
    problemaAnestesia: '',
    fumante: '',
    quantidadeCigarros: '',
    usaDentadura: '',
    usaLentesContato: '',
    usaPiercing: '',
    localizacaoPiercing: '',
    proteseCapilar: '',
    possibilidadeGravidez: '',
    informacoesRelevantes: '',
    cirurgiaId: searchParams.get('cirurgia_id') || searchParams.get('cirurgiaId') || ''
  })

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))

    if (field === campoBooleanoPendente && (value === 'sim' || value === 'nao')) {
      setCampoBooleanoPendente(null)
      marcarPrimeiroRadioPendente(null)
    }
  }

  /** Ao marcar Não, limpa o(s) campo(s) dependente(s) para não enviar dados obsoletos. */
  const handleSimNaoComDependente = (campoBooleano, camposDependentes, value) => {
    setFormData((prev) => {
      const next = { ...prev, [campoBooleano]: value }
      if (value !== 'sim') {
        for (const c of camposDependentes) {
          next[c] = ''
        }
      }
      return next
    })

    if (campoBooleano === campoBooleanoPendente && (value === 'sim' || value === 'nao')) {
      setCampoBooleanoPendente(null)
      marcarPrimeiroRadioPendente(null)
    }
  }

  // Função para converter bytes em MB
  const bytesToMB = (bytes) => {
    return (bytes / (1024 * 1024)).toFixed(2)
  }

  // Função para validar tipo de arquivo
  const isValidFileType = (file) => {
    const validTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/bmp',
      'image/tiff',
      'image/webp'
    ]
    return validTypes.includes(file.type)
  }

  // Função para adicionar arquivos
  const handleFileUpload = (files) => {
    const newFiles = Array.from(files)
    let newTotalSize = totalSize

    // Validar cada arquivo
    for (const file of newFiles) {
      if (!isValidFileType(file)) {
        alert(`Arquivo "${file.name}" não é um tipo válido. Apenas PDF e imagens são aceitos.`)
        return
      }

      newTotalSize += file.size
    }

    // Verificar limite de 20MB
    if (newTotalSize > 20 * 1024 * 1024) {
      alert('O tamanho total dos arquivos não pode exceder 20MB.')
      return
    }

    const newExames = newFiles.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type
    }))

    setExames(prev => [...prev, ...newExames])
    setTotalSize(newTotalSize)
  }

  // Função para remover arquivo
  const removeFile = (id) => {
    const fileToRemove = exames.find(exame => exame.id === id)
    if (fileToRemove) {
      setTotalSize(prev => prev - fileToRemove.size)
      setExames(prev => prev.filter(exame => exame.id !== id))
    }
  }

  // Funções para drag and drop
  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files)
    }
  }

  // Converte valor sim/nao para boolean (1/0 para FormData)
  const toBoolean = (value) => (value === 'sim' ? 1 : 0)

  const scrollToId = (id) => {
    if (!id) return
    const el = document.getElementById(id)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    if (typeof el.focus === 'function') el.focus()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const cirurgiaId = parseInt(searchParams.get('cirurgia_id') || searchParams.get('cirurgiaId') || '0', 10)
    if (!cirurgiaId) {
      alert('ID da cirurgia é obrigatório. Acesse o formulário através do link enviado.')
      return
    }

    const erroValidacao = obterErroValidacaoFormulario(formData)
    if (erroValidacao?.message) {
      alert(erroValidacao.message)
      setCampoBooleanoPendente(erroValidacao.pendingField || null)
      marcarPrimeiroRadioPendente(erroValidacao.scrollToId || null)
      if (erroValidacao.scrollToId) setTimeout(() => scrollToId(erroValidacao.scrollToId), 0)
      return
    }

    setCampoBooleanoPendente(null)
    marcarPrimeiroRadioPendente(null)

    setLoading(true)

    try {
      const formDataToSend = new FormData()

      // Dados obrigatórios no formato esperado pela API
      formDataToSend.append('cirurgia_id', cirurgiaId)
      formDataToSend.append('nome_completo', formData.nomeCompleto)
      formDataToSend.append('data_nascimento', formData.dataNascimento)
      formDataToSend.append('idade', parseInt(formData.idade, 10) || 0)
      formDataToSend.append('peso', parseFloat(formData.peso) || 0)
      formDataToSend.append('altura', parseFloat(formData.altura) || 0)
      formDataToSend.append('alergico_medicamento', toBoolean(formData.alergicoMedicamento))
      formDataToSend.append('comentario_alergia_medicamento', formData.comentario || '')
      formDataToSend.append('uso_medicamento_diario', toBoolean(formData.usoMedicamentoDiario))
      formDataToSend.append('medicamentos_em_uso', formData.medicamentosEmUso || '')
      formDataToSend.append('uso_calmantes', toBoolean(formData.usoCalmantes))
      formDataToSend.append('observacoes_medicamentos', formData.observacoes || '')
      formDataToSend.append('tosse_catarro', toBoolean(formData.tosseCatarro))
      formDataToSend.append('doenca_cardiaca', toBoolean(formData.doencaCardiaca))
      formDataToSend.append('marcapasso', toBoolean(formData.marcapasso))
      formDataToSend.append('problema_pressao', toBoolean(formData.problemaPressao))
      formDataToSend.append('problema_pulmao', toBoolean(formData.problemaPulmao))
      formDataToSend.append('problema_sangramento', toBoolean(formData.problemaSangramento))
      formDataToSend.append('diabetes', toBoolean(formData.diabetes))
      formDataToSend.append('doenca_infecto_contagiosa', toBoolean(formData.doencaInfectoContagiosa))
      formDataToSend.append('qual_doenca_infecto', formData.qualDoencaInfecto || '')
      formDataToSend.append('dor_lesao_coluna', toBoolean(formData.dorColuna))
      formDataToSend.append('problema_figado', toBoolean(formData.problemaFigado))
      formDataToSend.append('problema_rim', toBoolean(formData.problemaRim))
      formDataToSend.append('problema_sangue', toBoolean(formData.problemaSangue))
      formDataToSend.append('problema_tireoide', toBoolean(formData.problemaTireoide))
      formDataToSend.append('problema_estomago', toBoolean(formData.problemaEstomago))
      formDataToSend.append('dificuldade_abrir_boca', toBoolean(formData.dificuldadeAbrirBoca))
      formDataToSend.append('problema_dentario', toBoolean(formData.problemaDentario))
      formDataToSend.append('uso_drogas', toBoolean(formData.usoDrogas))
      formDataToSend.append('qual_droga', formData.qualDroga || '')
      formDataToSend.append('problema_neurologico', toBoolean(formData.problemaNeurologico))
      formDataToSend.append('cirurgias_anteriores', formData.cirurgiasAnteriores || '')
      formDataToSend.append('problema_anestesia', formData.problemaAnestesia || '')
      formDataToSend.append('fumante', toBoolean(formData.fumante))
      formDataToSend.append('quantidade_cigarros', formData.quantidadeCigarros || '')
      formDataToSend.append('usa_dentadura', toBoolean(formData.usaDentadura))
      formDataToSend.append('usa_lentes_contato', toBoolean(formData.usaLentesContato))
      formDataToSend.append('usa_piercing', toBoolean(formData.usaPiercing))
      formDataToSend.append('localizacao_piercing', formData.localizacaoPiercing || '')
      formDataToSend.append('protese_capilar', toBoolean(formData.proteseCapilar))
      formDataToSend.append(
        'possibilidade_gravidez',
        formData.sexo === 'masculino' ? 0 : toBoolean(formData.possibilidadeGravidez)
      )
      formDataToSend.append('informacoes_relevantes', formData.informacoesRelevantes || '')

      exames.forEach((exame, index) => {
        formDataToSend.append(`exame_${index}`, exame.file)
      })

      const response = await fetch(getApiUrl('/pre-operatorio'), {
        method: 'POST',
        body: formDataToSend
      })

      const data = await response.json().catch(() => ({}))

      if (response.ok) {
        navigate('/formulario-medico/sucesso')
      } else {
        const msg = data.error
          ? (typeof data.error === 'object' ? Object.values(data.error).flat().join(', ') : data.error)
          : 'Erro ao enviar formulário. Tente novamente.'
        alert(msg)
      }
    } catch (error) {
      console.error('Erro:', error)
      alert('Erro ao enviar formulário. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-center text-gray-900">
              Formulário Médico Pré-Anestésico
            </CardTitle>
            <p className="text-center text-gray-600">
              Preencha todos os campos com atenção. Suas informações são importantes para sua segurança.
            </p>
          </CardHeader>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informações Pessoais */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold">Informações Pessoais</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nomeCompleto">Nome Completo</Label>
                  <Input
                    id="nomeCompleto"
                    value={formData.nomeCompleto}
                    onChange={(e) => handleInputChange('nomeCompleto', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="dataNascimento">Data de Nascimento</Label>
                  <Input
                    id="dataNascimento"
                    type="date"
                    value={formData.dataNascimento}
                    onChange={(e) => handleInputChange('dataNascimento', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sexo</Label>
                  <RadioGroup
                    value={formData.sexo}
                    onValueChange={(value) => handleInputChange('sexo', value)}
                    className="flex flex-wrap gap-x-6 gap-y-2 mt-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="feminino" id="sexo-feminino" />
                      <Label htmlFor="sexo-feminino" className="font-normal cursor-pointer">
                        Feminino
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="masculino" id="sexo-masculino" />
                      <Label htmlFor="sexo-masculino" className="font-normal cursor-pointer">
                        Masculino
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="nao_informar" id="sexo-nao-informar" />
                      <Label htmlFor="sexo-nao-informar" className="font-normal cursor-pointer">
                        Não desejo informar
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
                <div>
                  <Label htmlFor="idade">Idade</Label>
                  <Input
                    id="idade"
                    type="number"
                    value={formData.idade}
                    onChange={(e) => handleInputChange('idade', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="altura">Altura (cm)</Label>
                  <Input
                    id="altura"
                    type="number"
                    value={formData.altura}
                    onChange={(e) => handleInputChange('altura', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="peso">Peso (kg)</Label>
                  <Input
                    id="peso"
                    type="number"
                    step="0.1"
                    value={formData.peso}
                    onChange={(e) => handleInputChange('peso', e.target.value)}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Alergias e Medicamentos */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold">Alergias e Medicamentos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>É alérgico a algum medicamento?</Label>
                <RadioGroup
                  value={formData.alergicoMedicamento}
                  onValueChange={(value) =>
                    handleSimNaoComDependente('alergicoMedicamento', ['comentario'], value)
                  }
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="alergico-sim" />
                    <Label htmlFor="alergico-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="alergico-nao" />
                    <Label htmlFor="alergico-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              {formData.alergicoMedicamento === 'sim' && (
                <div>
                  <Label htmlFor="comentario">Comentário sobre alergias (obrigatório)</Label>
                  <Textarea
                    id="comentario"
                    value={formData.comentario}
                    onChange={(e) => handleInputChange('comentario', e.target.value)}
                    placeholder="Descreva suas alergias..."
                    required
                  />
                </div>
              )}

              <div>
                <Label>Faz uso de algum medicamento?</Label>
                <p className="mt-1.5 text-xs font-medium leading-snug text-amber-700">
                  Lembrar de medicações para emagrecimento como Ozempic, Wegovy, Mounjaro, etc. e
                  anticoagulantes (remédios para afinar o sangue).
                </p>
                <RadioGroup
                  value={formData.usoMedicamentoDiario}
                  onValueChange={(value) =>
                    handleSimNaoComDependente('usoMedicamentoDiario', ['medicamentosEmUso'], value)
                  }
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="medicamento-sim" />
                    <Label htmlFor="medicamento-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="medicamento-nao" />
                    <Label htmlFor="medicamento-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              {formData.usoMedicamentoDiario === 'sim' && (
                <div>
                  <Label htmlFor="medicamentosEmUso">Liste os medicamentos e dose em uso (obrigatório)</Label>
                  <Textarea
                    id="medicamentosEmUso"
                    value={formData.medicamentosEmUso}
                    onChange={(e) => handleInputChange('medicamentosEmUso', e.target.value)}
                    placeholder="Ex: Dipirona 500mg - 1 comprimido 3x ao dia"
                    required
                  />
                </div>
              )}

              <div>
                <Label>Faz uso de tranquilizantes / calmantes?</Label>
                <RadioGroup
                  value={formData.usoCalmantes}
                  onValueChange={(value) =>
                    handleSimNaoComDependente('usoCalmantes', ['observacoes'], value)
                  }
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="calmantes-sim" />
                    <Label htmlFor="calmantes-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="calmantes-nao" />
                    <Label htmlFor="calmantes-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              {formData.usoCalmantes === 'sim' && (
                <div>
                  <Label htmlFor="observacoes">Observações sobre calmantes (obrigatório)</Label>
                  <Textarea
                    id="observacoes"
                    value={formData.observacoes}
                    onChange={(e) => handleInputChange('observacoes', e.target.value)}
                    placeholder="Descreva uso, dose ou frequência dos calmantes..."
                    required
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Problemas Respiratórios */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold">Problemas Respiratórios</CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label>Tem / teve recentemente tosse com catarro?</Label>
                <RadioGroup
                  value={formData.tosseCatarro}
                  onValueChange={(value) => handleInputChange('tosseCatarro', value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="tosse-sim" />
                    <Label htmlFor="tosse-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="tosse-nao" />
                    <Label htmlFor="tosse-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>
            </CardContent>
          </Card>

          {/* Problemas Cardíacos */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold">Problemas Cardíacos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Tem / teve alguma doença cardíaca?</Label>
                <RadioGroup
                  value={formData.doencaCardiaca}
                  onValueChange={(value) => handleInputChange('doencaCardiaca', value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="cardiaco-sim" />
                    <Label htmlFor="cardiaco-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="cardiaco-nao" />
                    <Label htmlFor="cardiaco-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>Tem marcapasso cardíaco?</Label>
                <RadioGroup
                  value={formData.marcapasso}
                  onValueChange={(value) => handleInputChange('marcapasso', value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="marcapasso-sim" />
                    <Label htmlFor="marcapasso-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="marcapasso-nao" />
                    <Label htmlFor="marcapasso-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>Tem / teve algum problema de pressão?</Label>
                <RadioGroup
                  value={formData.problemaPressao}
                  onValueChange={(value) => handleInputChange('problemaPressao', value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="pressao-sim" />
                    <Label htmlFor="pressao-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="pressao-nao" />
                    <Label htmlFor="pressao-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>
            </CardContent>
          </Card>

          {/* Outros Problemas de Saúde */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold">Outros Problemas de Saúde</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Tem / teve algum problema de pulmão? Asma, bronquite, tuberculose etc...</Label>
                <RadioGroup
                  value={formData.problemaPulmao}
                  onValueChange={(value) => handleInputChange('problemaPulmao', value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="pulmao-sim" />
                    <Label htmlFor="pulmao-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="pulmao-nao" />
                    <Label htmlFor="pulmao-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>Tem problema de sangramento?</Label>
                <RadioGroup
                  value={formData.problemaSangramento}
                  onValueChange={(value) => handleInputChange('problemaSangramento', value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="sangramento-sim" />
                    <Label htmlFor="sangramento-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="sangramento-nao" />
                    <Label htmlFor="sangramento-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>Tem diabetes?</Label>
                <RadioGroup
                  value={formData.diabetes}
                  onValueChange={(value) => handleInputChange('diabetes', value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="diabetes-sim" />
                    <Label htmlFor="diabetes-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="diabetes-nao" />
                    <Label htmlFor="diabetes-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>É portador de doença infecto-contagiosa? HIV, hepatite, etc...</Label>
                <RadioGroup
                  value={formData.doencaInfectoContagiosa}
                  onValueChange={(value) =>
                    handleSimNaoComDependente('doencaInfectoContagiosa', ['qualDoencaInfecto'], value)
                  }
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="infecto-sim" />
                    <Label htmlFor="infecto-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="infecto-nao" />
                    <Label htmlFor="infecto-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              {formData.doencaInfectoContagiosa === 'sim' && (
                <div>
                  <Label htmlFor="qualDoencaInfecto">Qual? (obrigatório)</Label>
                  <Input
                    id="qualDoencaInfecto"
                    value={formData.qualDoencaInfecto}
                    onChange={(e) => handleInputChange('qualDoencaInfecto', e.target.value)}
                    placeholder="Especifique a doença..."
                    required
                  />
                </div>
              )}

              <div>
                <Label>Tem dor ou lesão na coluna?</Label>
                <RadioGroup
                  value={formData.dorColuna}
                  onValueChange={(value) => handleInputChange('dorColuna', value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="coluna-sim" />
                    <Label htmlFor="coluna-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="coluna-nao" />
                    <Label htmlFor="coluna-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>Tem / teve problemas de Fígado? cirrose, hepatite, icterícia</Label>
                <RadioGroup
                  value={formData.problemaFigado}
                  onValueChange={(value) => handleInputChange('problemaFigado', value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="figado-sim" />
                    <Label htmlFor="figado-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="figado-nao" />
                    <Label htmlFor="figado-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>Tem / teve problemas de Rim? pedra, infecção, diálise</Label>
                <RadioGroup
                  value={formData.problemaRim}
                  onValueChange={(value) => handleInputChange('problemaRim', value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="rim-sim" />
                    <Label htmlFor="rim-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="rim-nao" />
                    <Label htmlFor="rim-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>Tem / teve problemas de Sangue? anemia, leucemia</Label>
                <RadioGroup
                  value={formData.problemaSangue}
                  onValueChange={(value) => handleInputChange('problemaSangue', value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="sangue-sim" />
                    <Label htmlFor="sangue-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="sangue-nao" />
                    <Label htmlFor="sangue-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>Tem / teve problemas de Tireoide?</Label>
                <RadioGroup
                  value={formData.problemaTireoide}
                  onValueChange={(value) => handleInputChange('problemaTireoide', value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="tireoide-sim" />
                    <Label htmlFor="tireoide-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="tireoide-nao" />
                    <Label htmlFor="tireoide-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>Tem / teve problemas de Estômago? hérnia de hiato, úlcera, queimação</Label>
                <RadioGroup
                  value={formData.problemaEstomago}
                  onValueChange={(value) => handleInputChange('problemaEstomago', value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="estomago-sim" />
                    <Label htmlFor="estomago-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="estomago-nao" />
                    <Label htmlFor="estomago-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>
            </CardContent>
          </Card>

          {/* Problemas Dentários */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold">Problemas Dentários</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Tem Dificuldade para abrir a boca?</Label>
                <RadioGroup
                  value={formData.dificuldadeAbrirBoca}
                  onValueChange={(value) => handleInputChange('dificuldadeAbrirBoca', value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="boca-sim" />
                    <Label htmlFor="boca-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="boca-nao" />
                    <Label htmlFor="boca-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>Tem / teve algum problema dentário?</Label>
                <RadioGroup
                  value={formData.problemaDentario}
                  onValueChange={(value) => handleInputChange('problemaDentario', value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="dentario-sim" />
                    <Label htmlFor="dentario-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="dentario-nao" />
                    <Label htmlFor="dentario-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>
            </CardContent>
          </Card>

          {/* Uso de Drogas */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold">Uso de Drogas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Faz uso de cocaína, maconha ou outra droga?</Label>
                <RadioGroup
                  value={formData.usoDrogas}
                  onValueChange={(value) => handleSimNaoComDependente('usoDrogas', ['qualDroga'], value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="drogas-sim" />
                    <Label htmlFor="drogas-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="drogas-nao" />
                    <Label htmlFor="drogas-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              {formData.usoDrogas === 'sim' && (
                <div>
                  <Label htmlFor="qualDroga">Qual droga? (obrigatório)</Label>
                  <Input
                    id="qualDroga"
                    value={formData.qualDroga}
                    onChange={(e) => handleInputChange('qualDroga', e.target.value)}
                    placeholder="Especifique..."
                    required
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Problemas Neurológicos */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold">Problemas Neurológicos</CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label>Tem / teve algum problema neurológico? Convulsão, desmaio, epilepsia</Label>
                <RadioGroup
                  value={formData.problemaNeurologico}
                  onValueChange={(value) => handleInputChange('problemaNeurologico', value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="neurologico-sim" />
                    <Label htmlFor="neurologico-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="neurologico-nao" />
                    <Label htmlFor="neurologico-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>
            </CardContent>
          </Card>

          {/* Histórico Cirúrgico */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold">Histórico Cirúrgico</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="cirurgiasAnteriores">Cite as cirurgias e a que tipo de anestesia que já foi submetido(a) anteriormente:</Label>
                <Textarea
                  id="cirurgiasAnteriores"
                  value={formData.cirurgiasAnteriores}
                  onChange={(e) => handleInputChange('cirurgiasAnteriores', e.target.value)}
                  placeholder="Ex: Apendicectomia em 2020 - anestesia geral"
                />
              </div>

              <div>
                <Label htmlFor="problemaAnestesia">Você ou algum familiar já teve problema com anestesia? Descreva:</Label>
                <Textarea
                  id="problemaAnestesia"
                  value={formData.problemaAnestesia}
                  onChange={(e) => handleInputChange('problemaAnestesia', e.target.value)}
                  placeholder="Descreva qualquer problema com anestesia..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Hábitos */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold">Hábitos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>É fumante?</Label>
                <RadioGroup
                  value={formData.fumante}
                  onValueChange={(value) =>
                    handleSimNaoComDependente('fumante', ['quantidadeCigarros'], value)
                  }
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="fumante-sim" />
                    <Label htmlFor="fumante-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="fumante-nao" />
                    <Label htmlFor="fumante-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              {formData.fumante === 'sim' && (
                <div>
                  <Label htmlFor="quantidadeCigarros">Quantos cigarros ou maços por dia? (obrigatório)</Label>
                  <Input
                    id="quantidadeCigarros"
                    value={formData.quantidadeCigarros}
                    onChange={(e) => handleInputChange('quantidadeCigarros', e.target.value)}
                    placeholder="Ex: 10 cigarros por dia"
                    required
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Próteses e Acessórios */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold">Próteses e Acessórios</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Usa dentadura ou prótese dentária?</Label>
                <RadioGroup
                  value={formData.usaDentadura}
                  onValueChange={(value) => handleInputChange('usaDentadura', value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="dentadura-sim" />
                    <Label htmlFor="dentadura-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="dentadura-nao" />
                    <Label htmlFor="dentadura-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>Usa lentes de contato?</Label>
                <RadioGroup
                  value={formData.usaLentesContato}
                  onValueChange={(value) => handleInputChange('usaLentesContato', value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="lentes-sim" />
                    <Label htmlFor="lentes-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="lentes-nao" />
                    <Label htmlFor="lentes-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label>Usa piercing?</Label>
                <RadioGroup
                  value={formData.usaPiercing}
                  onValueChange={(value) =>
                    handleSimNaoComDependente('usaPiercing', ['localizacaoPiercing'], value)
                  }
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="piercing-sim" />
                    <Label htmlFor="piercing-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="piercing-nao" />
                    <Label htmlFor="piercing-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>

              {formData.usaPiercing === 'sim' && (
                <div>
                  <Label htmlFor="localizacaoPiercing">Qual localização? (obrigatório)</Label>
                  <Input
                    id="localizacaoPiercing"
                    value={formData.localizacaoPiercing}
                    onChange={(e) => handleInputChange('localizacaoPiercing', e.target.value)}
                    placeholder="Ex: Nariz, língua, umbigo..."
                    required
                  />
                </div>
              )}

              <div>
                <Label>Tem prótese capilar, cílios ou unhas sintéticas (não naturais)?</Label>
                <RadioGroup
                  value={formData.proteseCapilar}
                  onValueChange={(value) => handleInputChange('proteseCapilar', value)}
                  className="flex space-x-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="protese-sim" />
                    <Label htmlFor="protese-sim">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="protese-nao" />
                    <Label htmlFor="protese-nao">Não</Label>
                  </div>
                </RadioGroup>
              </div>
            </CardContent>
          </Card>

          {/* Informações Adicionais */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold">Informações Adicionais</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {formData.sexo !== 'masculino' && (
                <div>
                  <Label>Mulher: caso sua cirurgia não esteja relacionada com o parto, ou curetagem, existe a possibilidade de estar grávida?</Label>
                  <RadioGroup
                    value={formData.possibilidadeGravidez}
                    onValueChange={(value) => handleInputChange('possibilidadeGravidez', value)}
                    className="flex space-x-4 mt-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="sim" id="gravidez-sim" />
                      <Label htmlFor="gravidez-sim">Sim</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="nao" id="gravidez-nao" />
                      <Label htmlFor="gravidez-nao">Não</Label>
                    </div>
                  </RadioGroup>
                </div>
              )}

              <div>
                <Label htmlFor="informacoesRelevantes">Compartilhe qualquer informação que julgue relevante.</Label>
                <Textarea
                  id="informacoesRelevantes"
                  value={formData.informacoesRelevantes}
                  onChange={(e) => handleInputChange('informacoesRelevantes', e.target.value)}
                  placeholder="Outras informações que considera importantes..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Anexos de Exames */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold">Exames Pré-Operatórios</CardTitle>
              <p className="text-sm text-gray-600">
                Anexe seus exames em PDF ou imagem. Tamanho máximo total: 20MB
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Área de Upload */}
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  dragActive 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-lg font-medium text-gray-900 mb-2">
                  Arraste e solte seus arquivos aqui
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  ou clique para selecionar arquivos
                </p>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.webp"
                  onChange={(e) => handleFileUpload(e.target.files)}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md cursor-pointer inline-block"
                >
                  Selecionar Arquivos
                </label>
                <p className="text-xs text-gray-400 mt-2">
                  Formatos aceitos: PDF, JPG, PNG, GIF, BMP, TIFF, WEBP
                </p>
              </div>

              {/* Informações de tamanho */}
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">
                  Tamanho total: {bytesToMB(totalSize)} MB / 20 MB
                </span>
                <span className={`font-medium ${
                  totalSize > 15 * 1024 * 1024 ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {exames.length} arquivo(s) selecionado(s)
                </span>
              </div>

              {/* Lista de arquivos */}
              {exames.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-900">Arquivos anexados:</h4>
                  {exames.map((exame) => (
                    <div
                      key={exame.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        {exame.type === 'application/pdf' ? (
                          <FileText className="h-5 w-5 text-red-500" />
                        ) : (
                          <Image className="h-5 w-5 text-blue-500" />
                        )}
                        <div>
                          <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                            {exame.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {bytesToMB(exame.size)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(exame.id)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Botão de Envio */}
          <div className="flex justify-center">
            <Button
              type="submit"
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-xl"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-5 w-5" />
                  Enviar Formulário
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default FormularioMedico 